import { get, set } from "lodash";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import {
  HOUSEHOLD_INCOME_OPTIONS,
  PREVOYANCE_KNOWLEDGE_OPTIONS,
} from "../../../obseques";

const Information = ({
  bindField,
  initialValues,
  setPercent,
  isFieldRequired,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const INFORMATION = t(
    "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.INFORMATION",
    {
      returnObjects: true,
    },
  );
  const [isPPE, setIsPPE] = useState(
    get(initialValues, "project.fields.isPPE"),
  );
  const householdIncomePath = "project.fields.householdIncome";
  const householdIncomeField = bindField(householdIncomePath, {
    label: SUBSCRIPTION.FORM.HOUSEHOLD_INCOME.LABEL,
    required: isFieldRequired(householdIncomePath),
    onChange: () => setPercent(initialValues),
  });
  const monthlyChargesPath = "project.fields.monthlyCharges";
  const monthlyChargesField = bindField(monthlyChargesPath, {
    label: SUBSCRIPTION.FORM.MONTHLY_CHARGES.LABEL,
    required: isFieldRequired(monthlyChargesPath),
    onBlur: () => setPercent(initialValues),
  });
  const futureExpensesPath = "project.fields.futureExpenses";
  const futureExpensesField = bindField(futureExpensesPath, {
    label: SUBSCRIPTION.FORM.FUTURE_EXPENSES.LABEL,
    required: isFieldRequired(futureExpensesPath),
    onBlur: () => setPercent(initialValues),
  });
  const prevoyanceKnowledgePath = "project.fields.prevoyanceKnowledge";
  const prevoyanceKnowledgeField = bindField(prevoyanceKnowledgePath, {
    label: SUBSCRIPTION.FORM.PREVOYANCE_KNOWLEDGE.LABEL,
    required: isFieldRequired(prevoyanceKnowledgePath),
    onChange: () => setPercent(initialValues),
  });

  const buildSwitchField = (path, label, onChangeExtra) => {
    const field = bindField(path, {
      label,
      required: isFieldRequired(path),
      onChange: (value) => {
        if (typeof onChangeExtra === "function") {
          onChangeExtra(value);
        }
        setPercent(initialValues);
      },
    });
    return (
      <FormField
        key={path}
        path={field.path}
        label={field.label}
        required={field.required}
        disabled={field.disabled}
      >
        {(inputField) => (
          <Input
            type="switch"
            label={label}
            checked={get(initialValues, path) === true}
            {...inputField}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      </FormField>
    );
  };

  const ppeFunctionPath = "project.fields.ppeFunction";
  const ppeFunctionField = bindField(ppeFunctionPath, {
    label: "Quelles sont / étaient les fonctions du prospect ?",
    required: false,
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION}-section`}
    >
      <Form type="vertical" title={INFORMATION.TITLE}>
        <div className="grid--1">
          {buildSwitchField(
            "project.fields.isPPE",
            "Personne Politiquement Exposée (PPE*)",
            (value) => {
              setIsPPE(value);
              if (!value) {
                set(initialValues, "project.fields.ppeFunction", "");
                set(initialValues, "project.fields.ppeRelated", false);
              }
            },
          )}
          <Alert
            showIcon
            type="info"
            message={
              "* Une Personne Politiquement Exposée (PPE) est une personne qui exerce ou a cessé d'exercer depuis moins d'un an, une fonction publique importante en France, dans un pays étranger ou au sein d'une organisation internationale (ex. chef d'État, ministre, parlementaire, magistrat, dirigeant d'entreprise publique…), ainsi que certains membres de sa famille (conjoint, enfants, parents) ou les personnes connues pour lui être étroitement associées."
            }
          />
          {isPPE && (
            <FormField
              path={ppeFunctionField.path}
              label={ppeFunctionField.label}
              required={ppeFunctionField.required}
              disabled={ppeFunctionField.disabled}
            >
              {(field) => (
                <Input
                  label={ppeFunctionField.label}
                  placeholder={"Saisir la fonction du prospect"}
                  type="custom"
                  normalizeText={true}
                  defaultValue={ppeFunctionField.defaultValue}
                  {...field}
                  onChange={ppeFunctionField.onChange}
                  onBlur={ppeFunctionField.onBlur}
                  optional
                />
              )}
            </FormField>
          )}
          {buildSwitchField(
            "project.fields.ppeRelated",
            "Lien familial ou association étroite du prospect avec une PPE* ?",
          )}
          <FormField
            path={householdIncomeField.path}
            label={householdIncomeField.label}
            required={householdIncomeField.required}
            disabled={householdIncomeField.disabled}
          >
            {(field) => (
              <Select
                type="simple"
                label={householdIncomeField.label}
                options={HOUSEHOLD_INCOME_OPTIONS}
                value={get(initialValues, householdIncomePath)}
                {...field}
                onChange={householdIncomeField.onChange}
                onBlur={householdIncomeField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={monthlyChargesField.path}
            label={monthlyChargesField.label}
            required={monthlyChargesField.required}
            disabled={monthlyChargesField.disabled}
          >
            {(field) => (
              <Input
                type="number"
                label={monthlyChargesField.label}
                placeholder={SUBSCRIPTION.FORM.MONTHLY_CHARGES.PLACEHOLDER}
                defaultValue={monthlyChargesField.defaultValue}
                {...field}
                onChange={monthlyChargesField.onChange}
                onBlur={monthlyChargesField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={futureExpensesField.path}
            label={futureExpensesField.label}
            required={futureExpensesField.required}
            disabled={futureExpensesField.disabled}
          >
            {(field) => (
              <Input
                type="number"
                label={futureExpensesField.label}
                placeholder={SUBSCRIPTION.FORM.FUTURE_EXPENSES.PLACEHOLDER}
                defaultValue={futureExpensesField.defaultValue}
                {...field}
                onChange={futureExpensesField.onChange}
                onBlur={futureExpensesField.onBlur}
              />
            )}
          </FormField>
          <FormField
            path={prevoyanceKnowledgeField.path}
            label={prevoyanceKnowledgeField.label}
            required={prevoyanceKnowledgeField.required}
            disabled={prevoyanceKnowledgeField.disabled}
          >
            {(field) => (
              <Select
                type="simple"
                label={prevoyanceKnowledgeField.label}
                options={PREVOYANCE_KNOWLEDGE_OPTIONS}
                value={get(initialValues, prevoyanceKnowledgePath)}
                {...field}
                onChange={prevoyanceKnowledgeField.onChange}
                onBlur={prevoyanceKnowledgeField.onBlur}
              />
            )}
          </FormField>
          {buildSwitchField(
            "project.fields.acceptProductStatuses",
            "Le prospect reconnaît avoir pris connaissance du contenu de la fiche d'information et de conseils préalablement à l'adhésion au contrat d'assurance Prévoyance décès / Garantie Obsèques, avoir reçu et pris connaissance du document d'information sur le produit d'assurance qui lui est proposé à l'adhésion, ainsi qu'une information détaillée sur l'étendue et la définition des risques et garanties proposés, en accepter les termes et en avoir conservé un exemplaire.",
          )}
          {buildSwitchField(
            "project.fields.acceptAgreement",
            "Le prospect atteste sur l'honneur de l'exactitude des renseignements complétés.",
          )}
          {buildSwitchField(
            "project.fields.acceptFees",
            "Le prospect reconnaît avoir été informé, conformément à l'article L.521-6 du Code des assurances, de la possibilité de recevoir les informations et documents relatifs au contrat sur un support durable autre que le papier, et avoir choisi le support numérique durable. Il atteste de la validité de l'adresse e-mail fournie.",
          )}
        </div>
      </Form>
    </div>
  );
};

export default Information;
